import { apply, give, has, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { clock, m } from '../i18n';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { dateEnglish, dateLabel, dateLong, dayNum } from '../content/calendar';
import { isRaining, tempAt, typhoonActive, typhoonDay, typhoonTomorrow, WEATHER_ICON, weatherFor, weatherNow } from '../content/weather';
import { finishKit, KIT_ITEMS, kitDone } from '../content/disasters';
import { say } from './dialog';
import { h } from './dom';
import { isPanelOpen, openPanel, refreshPanel } from './panel';

const t = () => m().sky;

/** The tear-off calendar: today's date and weather, tomorrow's forecast, and a line to say in English. */
export function openCalendar(): void {
  const c = t().calendar;
  const k = dayNum();
  const now = weatherNow();
  const tw = weatherFor(k + 1);
  if (onceToday('sky.calendar')) {
    sfx('rustle');
    apply({ study: 1 }, { float: false });
  }
  const text = [
    `${WEATHER_ICON[now.kind]} ${c.today(dateLong(k), t().kinds[now.kind], now.temp, now.rainPct)}`,
    `${WEATHER_ICON[tw.kind]} ${c.tomorrow(t().kinds[tw.kind], tw.lo, tw.hi, tw.rainPct)}`,
    '',
    c.tear,
  ].join('\n');
  const line = `${c.sayDate(dateEnglish(k))} ${c.say[now.kind]}`;
  say({
    who: c.who,
    icon: '📅',
    text,
    choices: [{ text: `🗣️ ${c.practice}: “${line}”`, keepOpen: true, run: () => sayEnglish(line) }, { text: m().common.ok }],
  });
}

/** The emergency kit by the door: the typhoon checklist on typhoon days, a reminder otherwise. */
export function openKit(): void {
  const k = t().kit;
  if (!typhoonDay()) {
    sfx('pickup');
    say({ who: m().sky.objects.sky_kit, icon: '🎒', text: typhoonTomorrow() ? `${k.calm}\n\n🌀 ${k.calmTomorrow}` : k.calm });
    return;
  }
  openPanel('sky_kit', renderKit);
}

function renderKit() {
  const k = t().kit;
  const done = KIT_ITEMS.filter(kitDone).length;
  const rows = KIT_ITEMS.map((key) => {
    const it = k.items[key];
    const ok = kitDone(key);
    let action: Node;
    if (ok) action = h('span', { class: 'ok' }, k.done);
    else if (key === 'plants') action = h('span', { class: 'hint' }, `➡️ ${k.goBalcony}`);
    else
      action = h(
        'button',
        {
          class: 'btn small green',
          onclick: () => {
            finishKit(key);
            if (isPanelOpen('sky_kit')) refreshPanel('sky_kit');
          },
        },
        `${k.go} · 10′`,
      );
    // English practice line under each job.
    return h('li', { class: ok ? 'done' : '' }, h('span', { class: 'ic' }, it.icon), h('div', { class: 'tx' }, it.label, h('small', null, `“${it.en}”`)), action);
  });
  return {
    title: k.title,
    className: 'sky-kit-panel',
    body: [
      h('p', { class: 'sky-kit-intro' }, done === KIT_ITEMS.length ? `✅ ${k.all}` : k.intro),
      h('div', { class: 'sky-kit-bar' }, h('i', { style: `width:${(done / KIT_ITEMS.length) * 100}%` })),
      h('ul', { class: 'sky-kit' }, rows),
    ],
  };
}

export const UMBRELLA_PRICE = 35;

/** The umbrella bucket by the store door. */
export function openUmbrellaStand(): void {
  const u = t().umbrella;
  if (has('umbrella')) {
    say({ who: u.who, icon: '☂️', text: u.have });
    return;
  }
  say({
    who: u.who,
    icon: '☂️',
    text: u.ask(UMBRELLA_PRICE),
    choices: [
      {
        text: u.buy(UMBRELLA_PRICE),
        disabled: S().coins < UMBRELLA_PRICE,
        run: () => {
          if (S().coins < UMBRELLA_PRICE) return toast(u.poor);
          sfx('checkout');
          apply({ coins: -UMBRELLA_PRICE }, { float: false });
          give('umbrella');
          toast(u.bought);
        },
      },
      { text: u.no },
    ],
  });
}

/** Bag "Use" on the umbrella. */
export function useUmbrella(): void {
  if (isRaining() || typhoonActive()) {
    sfx('umbrella');
    toast(t().umbrella.useRain);
  } else toast(t().umbrella.useDry);
}

/** Forecast block for the TV news: today, tomorrow and a tip (plus the typhoon alert). */
export function forecastBlock(): Node {
  const f = t().forecast;
  const k = dayNum();
  const card = (label: string, day: number, temp: number | null) => {
    const w = weatherFor(day);
    const sub = f.rain(w.rainPct);
    return h(
      'div',
      { class: `day ${w.kind}` },
      h('span', { class: 'ic' }, WEATHER_ICON[w.kind]),
      h(
        'div',
        { class: 'tx' },
        h('b', null, label),
        h('span', null, t().kinds[w.kind]),
        h('small', null, `${temp !== null ? `${temp}°C` : f.range(w.lo, w.hi)} · ${sub}`),
        h('em', null, dateLabel(day)),
      ),
    );
  };
  const today = weatherFor(k);
  // Forecasts round to the half hour.
  const half = (v: number) => clock(Math.min(24 * 60 - 30, Math.round(v / 30) * 30));
  const when = today.rain && today.kind !== 'typhoon' && today.rain.to > S().time ? `${f.showers(half(today.rain.from), half(today.rain.to))}. ` : '';
  return h(
    'div',
    null,
    typhoonTomorrow() ? h('p', { class: 'sky-alert' }, f.alert) : null,
    h('div', { class: 'sky-fc' }, card(f.today, k, tempAt(k, S().time)), card(f.tomorrow, k + 1, null), h('p', { class: 'tip' }, `💡 ${when}${f.tips[typhoonTomorrow() ? 'typhoon' : today.kind]}`)),
  );
}
